import { CustomerBagScanError } from '@curby/shared';
import { useSet } from '@mantine/hooks';
import React, { createContext, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { CustomerBagScanMutation, useCustomerBagScanMutation } from './graphql.generated';

// Define the shape of your context state
export interface BagScanContextValue {
	page: CustomerScanSubRoute;
	setPage: React.Dispatch<React.SetStateAction<CustomerScanSubRoute>>;
	handleScan: (code: string) => void;
	scannedBag: CustomerBagScanMutation['scanAssetFromUser'] | null;
	loading: boolean;
	error: CustomerBagScanError | null;
	setError: React.Dispatch<React.SetStateAction<CustomerBagScanError | null>>;
	setScannedBag: React.Dispatch<
		React.SetStateAction<CustomerBagScanMutation['scanAssetFromUser'] | null>
	>;
	scanAnotherBag: () => void;
	showCodeEntry: boolean;
	setShowCodeEntry: React.Dispatch<React.SetStateAction<boolean>>;
}

export type CustomerScanSubRoute = 'SCAN' | 'RESULT' | 'MATERIAL_CONFIRM' | 'INSTRUCTIONS';

export type BagScanStatus =
	| 'PENDING'
	| 'VERIFYING'
	| 'CONFIRMED'
	| 'DUPLICATE'
	| 'ERROR'
	| 'INVALID'
	| null;

// Create the context with undefined as default
// eslint-disable-next-line react-refresh/only-export-components
export const BagScanContext = createContext<BagScanContextValue | undefined>(undefined);

// Provider component props
interface BagScanContextProviderProps {
	children: ReactNode;
	initialCode?: string;
}

// Provider component
export const BagScanContextProvider: React.FC<BagScanContextProviderProps> = ({
	initialCode,
	children,
}) => {
	const [page, setPage] = useState<CustomerScanSubRoute>('INSTRUCTIONS');
	const ignoreCode = useSet<string>([]);
	const invalidCodes = useSet();
	const currentCode = useRef<string | null>(null);
	const [scannedBag, setScannedBag] = useState<CustomerBagScanMutation['scanAssetFromUser'] | null>(
		null
	);
	const [showCodeEntry, setShowCodeEntry] = useState(false);
	const [error, setError] = useState<CustomerBagScanError | null>(null);
	const [customerBagScanMutation, { loading }] = useCustomerBagScanMutation({
		onCompleted: (data) => {
			if (data.scanAssetFromUser) {
				setScannedBag(data.scanAssetFromUser);
				currentCode.current = null;
				if (!data.scanAssetFromUser.material) {
					setPage('MATERIAL_CONFIRM');
				} else {
					setPage('RESULT');
				}
			}
		},
		onError: (error) => {
			setError(error.message as CustomerBagScanError);
			switch (error.message as CustomerBagScanError) {
				case 'ALREADY_SCANNED':
					return;
				case 'INVALID_CODE':
					invalidCodes.add(currentCode.current);
					break;
				case 'NOT_AUTHORISED':
					break;
			}

			currentCode.current = null;
		},
	});

	const handleScan = useCallback(
		async (code: string) => {
			if (!ignoreCode.has(code)) {
				ignoreCode.add(code);
				currentCode.current = code;
				customerBagScanMutation({
					variables: {
						input: {
							scannableCode: code,
						},
					},
				});
			} else if (invalidCodes.has(code)) {
				setError('INVALID_CODE');
				return;
			} else if (ignoreCode.has(code)) {
				setError('ALREADY_SCANNED');
				return;
			}
		},
		[customerBagScanMutation, ignoreCode, invalidCodes, setError]
	);

	useEffect(() => {
		if (initialCode) {
			handleScan(initialCode);
		}
	}, [initialCode, handleScan]);

	const scanAnotherBag = () => {
		setScannedBag(null);
		setError(null);
		setPage('SCAN');
	};

	return (
		<BagScanContext.Provider
			value={{
				page,
				setPage,
				handleScan,
				scannedBag,
				loading,
				error,
				setScannedBag,
				setError,
				scanAnotherBag,
				showCodeEntry,
				setShowCodeEntry,
			}}
		>
			{children}
		</BagScanContext.Provider>
	);
};
